import { Routes, Route, Navigate } from "react-router-dom";

import RequireAuth from "./Components/Utils/RequireAuth";
import RequirePermission from "./Components/Utils/RequirePermission";
import Layout from "./Layout/Layout";
import SiteLayout from "./Layout/SiteLayout";
import { ALL_FIRM_TAB_PERMISSIONS, ALL_DEALER_FIRM_TAB_PERMISSIONS } from "./utils/firmUtils";

import mascotNoInternet from "./Assets/icons/okkali_mascot_nointernet.png";
import mascotRepairServer from "./Assets/icons/okkali_mascot_repairserver.png";
import DashBoardPage from "./Pages/dashboard/storageRoles/DashboardPage";
import DealerDashboardPage from "./Pages/dashboard/dealerRoles/DashboardPage";
import AlertsPage from "./Pages/alerts/storageRoles/AlertsPage";
import DealerFirmPage from "./Pages/Firm/dealerRoles/FirmPage";
import FirmPage from "./Pages/Firm/storageRoles/FirmPage";
import FirmDetailRoutePage from "./Pages/Firm/storageRoles/FirmDetailPage";
import DealerFirmDetailPage from "./Pages/Firm/dealerRoles/FirmDetailPage";
import ProfilePage from "./Pages/ProfilePage";
import BillPage from "./Pages/BillPage";
import UsersPage from "./Pages/User/UsersPage";
import UserDetailRoutePage from "./Pages/User/UserDetailRoutePage";
import DetailDevicePage from "./Pages/DetailDevice/DetailDevicePage";
import DealerDetailDevicePage from "./Pages/DetailDevice/dealerRoles/DetailDevicePage";
import StorageDeviceDetailRoutePage from "./Pages/DetailDevice/storageRoles/DeviceDetailRoutePage";
import DealerDeviceDetailRoutePage from "./Pages/DetailDevice/dealerRoles/DeviceDetailRoutePage";
import ProductInfoPage from "./Pages/Parameters/Product/Info/ProductInfoPage";
import TownPage from "./Pages/Parameters/Utils/TownPage";
import CityPage from "./Pages/Parameters/Utils/CityPage";
import ProductTypePage from "./Pages/Parameters/Product/Type/ProductTypePage";
import LoginPage from "./Pages/Login/LoginPage";
import ResetPasswordPage from "./Pages/Login/ResetPasswordPage";
import InvitationPage from "./Pages/Login/InvitationPage";
import Page404 from "./Pages/Page404";
import AppUpdateGate from "./Components/shared/AppUpdateGate";
import PrivacyPolicyPage from "./Pages/PrivacyPolicyPage";
import OfferGeneratorPage from "./Pages/OfferGeneratorPage";
import OfferSelectionPage from "./Pages/offer/OfferSelectionPage";
import SimpleOfferPage from "./Pages/offer/SimpleOfferPage";
import PaymentsPage from "./Pages/payments/storageRoles/PaymentsPage";
import SalesDashboardPage from "./Pages/salesDashboard/storageRoles/SalesDashboardPage";
import PendingDevicesPage from "./Pages/pendingDevices/storageRoles/PendingDevicesPage";
import FactoryOverviewPage from "./Pages/factory/storageRoles/FactoryOverviewPage";
import FactoryPartsPage from "./Pages/factory/storageRoles/FactoryPartsPage";
import FactoryMachinesPage from "./Pages/factory/storageRoles/FactoryMachinesPage";
import FactoryZonesPage from "./Pages/factory/storageRoles/FactoryZonesPage";
import FactoryCatalogPage from "./Pages/factory/storageRoles/FactoryCatalogPage";
import FactoryDocumentsPage from "./Pages/factory/storageRoles/FactoryDocumentsPage";
import ProgramlamaIslemleriPage from "./Pages/factory/storageRoles/ProgramlamaIslemleriPage";
import EmqxPage from "./Pages/emqx/storageRoles/EmqxPage";
import AuditLogPage from "./Pages/audit/storageRoles/AuditLogPage";
import TasksPage from "./Pages/tasks/storageRoles/TasksPage";
import DevelopmentPage from "./Pages/development/storageRoles/DevelopmentPage";
import PushBroadcastPage from "./Pages/push/storageRoles/PushBroadcastPage";

import ReportPage from "./Pages/Reports/reports-page";
import ReportFirmRemainderPage from "./Pages/Reports/Firm/report-firm-remainder-page";
import FirmPerformanceUnifiedReportPage from "./Pages/Reports/Firm/FirmPerformanceUnifiedReportPage";
import ReportNewFirmsPage from "./Pages/Reports/Firm/report-firm-new-page";
import ReportFirmListBasedLocationPage from "./Pages/Reports/Firm/report-firm-list-based-location-page";
import ReportFirmUploadedBillPage from "./Pages/Reports/Firm/report-firm-uploaded-bill-page";
import ReportDeviceConnectionInfo from "./Pages/Reports/Device/report-device-connection-info-page";
import ReportDeviceGeneralInfo from "./Pages/Reports/Device/report-device-general-info-page";
import ReportNewDevicesBetweenDatesPage from "./Pages/Reports/Device/report-device-new-page";
import DevicesAtDateReportPage from "./Pages/Reports/Device/DevicesAtDateReportPage";
import ReportDevicesStatusBetweenDatesPage from "./Pages/Reports/Device/report-device-status-page";
import ReportDevicesServiceStatusBetweenDatesPage from "./Pages/Reports/Device/report-device-service-status-page";
import ReportDevicesPerformancePage from "./Pages/Reports/Device/report-device-performance-page";

import FirmLayout from "./Layout/Firm/FirmLayout";
import UserLayout from "./Layout/UserLayout";
import DetailDeviceLayout from "./Layout/DetailDevice/DetailDeviceLayout";
import ReportsLayout from "./Layout/Reports/ReportsLayout";
import useAuth from "./Hooks/useAuth";
import { resolveLandingPath } from "./utils/landing";
import logger from "./utils/logger";

[mascotNoInternet, mascotRepairServer].forEach((src) => {
  const img = new Image();
  img.src = src;
});

const ROLES = {
  Storage: "storage",
  Admin: "admin",
  Dealer: "dealer",
};

function DashboardRouter() {
  const { auth } = useAuth();
  if (auth?.role === "dealer") return <DealerDashboardPage />;
  return <DashBoardPage />;
}

function FirmDetailRouter() {
  const { auth } = useAuth();
  if (auth?.role === "dealer") return <DealerFirmDetailPage />;
  return <FirmDetailRoutePage />;
}

function FirmRouter() {
  const { auth } = useAuth();
  if (auth?.role === "dealer") return <DealerFirmPage />;
  return <FirmPage />;
}

// "/Anasayfa" bare path'ine (index) düşen kullanıcıyı yetkisine göre ilk
// erişilebilir sayfaya yönlendirir. Bu route yoksa bare "/Anasayfa" catch-all
// Page404'e düşüyordu (ör. eski bookmark veya re-login sonrası state.from).
function AnasayfaIndex() {
  const { auth } = useAuth();
  const target = resolveLandingPath(auth);
  logger.warn("[AnasayfaIndex] bare /Anasayfa yönlendirmesi:", target);
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <>
      {/* Panel güncelleme kapısı — yeni dağıtım çıktığında zorunlu yenileme,
          yeni sürüm notlarını bir kez gösterme. Route'ların DIŞINDA durur ki
          hangi sayfada olunursa olunsun çalışsın. */}
      <AppUpdateGate />
      <Routes>
      <Route path="/" element={<Layout />}>
        <Route path="" element={<LoginPage />} />
        <Route path="sifre-sifirla" element={<ResetPasswordPage />} />
        <Route path="davet" element={<InvitationPage />} />
        <Route path="gizlilik-politikasi" element={<PrivacyPolicyPage />} />
      </Route>

      <Route
        element={
          <RequireAuth
            allowedRoles={[
              ROLES.Storage,
              ROLES.Admin,
              ROLES.Dealer,
            ]}
          />
        }
      >
        <Route path="Anasayfa" element={<SiteLayout />}>
          <Route index={true} element={<AnasayfaIndex />} />

          {/* ═══ Ortak — Tüm Roller ═══ */}
          <Route
            element={
              <RequireAuth
                allowedRoles={[
                  ROLES.Storage,
                  ROLES.Admin,
                  ROLES.Dealer,
                ]}
              />
            }
          >
            <Route path="Profil" element={<ProfilePage />} />
          </Route>

          {/* ═══ Storage + Dealer Ortak (aynı path, farklı component) ═══ */}
          <Route
            element={
              <RequireAuth
                allowedRoles={[ROLES.Storage, ROLES.Admin, ROLES.Dealer]}
              />
            }
          >
            <Route element={<RequirePermission permission="dashboard.read" />}>
              <Route path="Dashboard" element={<DashboardRouter />} />
            </Route>
            <Route path="Firma" element={<FirmLayout />}>
              <Route element={<RequirePermission permission="firms.read" />}>
                <Route index={true} element={<FirmRouter />} />
              </Route>
              {/* Firma detayına storage (firms.read/tab izinleri) hem de bayi
                  (dealers/customers tab izinleri) girebilir — FirmCard.js'deki
                  canNavigate ile aynı izin setleri. */}
              <Route
                element={
                  <RequirePermission
                    anyOf={[
                      "firms.read",
                      ...ALL_FIRM_TAB_PERMISSIONS,
                      ...ALL_DEALER_FIRM_TAB_PERMISSIONS,
                    ]}
                  />
                }
              >
                <Route path=":firmId" element={<FirmDetailRouter />} />
              </Route>
            </Route>
          </Route>

          {/* ═══ Dealer Routes ═══ */}
          <Route
            element={
              <RequireAuth allowedRoles={[ROLES.Dealer]} />
            }
          >
            <Route path="Cihazlarım" element={<DetailDeviceLayout />}>
              {/* Liste: cihaz listesi görüntüleme izni */}
              <Route element={<RequirePermission permission="devices.read" />}>
                <Route index={true} element={<DealerDetailDevicePage />} />
              </Route>
              {/* Detay: herhangi bir cihaz sekmesi izni yeterli — ayrıca
                  devices.read şartı yok (sekme izni varsa cihaza girip o sekmeyi görebilmeli) */}
              <Route
                element={
                  <RequirePermission
                    anyOf={[
                      "devices.read", "devices.info", "devices.parts",
                      "devices.location", "devices.settings", "devices.maintenance", "devices.stats", "devices.reports",
                    ]}
                  />
                }
              >
                <Route path=":deviceId" element={<DealerDeviceDetailRoutePage />} />
              </Route>
            </Route>
            {/* Menüde "Firmalar", bayi için Müşterilerim + Bayilerim olarak
                ikiye ayrılır. Firma detayı ise ortak /Anasayfa/Firma/:firmId
                route'unda kalır (FirmCard oraya link verir). Her liste kendi
                taksonomi iznini kontrol eder (eski ortak firms.read kaldırıldı). */}
            <Route element={<RequirePermission permission="customers.read" />}>
              <Route path="Müşterilerim" element={<FirmLayout />}>
                <Route index={true} element={<DealerFirmPage variant="customers" />} />
              </Route>
            </Route>
            <Route element={<RequirePermission permission="dealers.read" />}>
              <Route path="Bayilerim" element={<FirmLayout />}>
                <Route index={true} element={<DealerFirmPage variant="dealers" />} />
              </Route>
            </Route>
          </Route>

          {/* ═══ Storage Routes ═══ */}
          <Route
            element={
              <RequireAuth allowedRoles={[ROLES.Storage, ROLES.Admin]} />
            }
          >
            {/* Uyarılar (#229) — menüde DASHBOARD grubunda; bayide yok */}
            <Route element={<RequirePermission permission="alerts.read" />}>
              <Route path="Dashboard/Uyarilar" element={<AlertsPage />} />
            </Route>
            <Route path="Cihazlar" element={<DetailDeviceLayout />}>
              {/* Liste: cihaz listesi görüntüleme izni */}
              <Route element={<RequirePermission permission="devices.read" />}>
                <Route index={true} element={<DetailDevicePage />} />
              </Route>
              {/* Detay: herhangi bir cihaz sekmesi izni yeterli */}
              <Route
                element={
                  <RequirePermission
                    anyOf={[
                      "devices.read", "devices.info", "devices.parts",
                      "devices.location", "devices.settings", "devices.maintenance", "devices.stats", "devices.reports",
                    ]}
                  />
                }
              >
                <Route path=":deviceId" element={<StorageDeviceDetailRoutePage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission permission="bills.read" />}>
              <Route path="Fatura" element={<BillPage />} />
            </Route>
            <Route element={<RequirePermission permission="sales.read" />}>
              <Route path="Satış" element={<SalesDashboardPage />} />
            </Route>
            <Route element={<RequirePermission permission="users.read" />}>
              <Route path="Kullanıcı" element={<UserLayout />}>
                <Route index={true} element={<UsersPage />} />
                <Route path=":userId" element={<UserDetailRoutePage />} />
              </Route>
            </Route>
            <Route element={<RequirePermission permission="offers.create" />}>
              <Route path="Teklif" element={<OfferSelectionPage />} />
              <Route path="Teklif/Sade" element={<SimpleOfferPage />} />
              <Route path="Teklif/Detayli" element={<OfferGeneratorPage />} />
              {/* Eski link/yer imleri için geriye dönük alias */}
              <Route path="TeklifOluştur" element={<OfferGeneratorPage />} />
            </Route>
            <Route element={<RequirePermission permission="wallet.topup" />}>
              <Route path="Ödemeler" element={<PaymentsPage />} />
              {/* Eski link/yer imleri için geriye dönük alias — Ödemeler
                  sayfasının sekmeleri. "Satış" alias'ı YOK: o yol artık satış
                  dashboard'una ait (makine satışları Ödemeler > Satış / İade
                  sekmesinde). */}
              <Route path="BakiyeYükleme" element={<PaymentsPage />} />
              <Route path="MakineKayıtları" element={<PaymentsPage />} />
            </Route>
            {/* Server tarafı bu sayfayı pendingDevices.read ile koruyor
                (pending-device-base.routes.js). Route devices.write istiyordu:
                yalnızca pendingDevices.read'i olan kullanıcı menüde görüyor
                ama sayfayı açamıyordu. */}
            <Route element={<RequirePermission permission="pendingDevices.read" />}>
              <Route
                path="OnayBekleyenCihazlar"
                element={<PendingDevicesPage />}
              />
            </Route>
            {/* Fabrika (Okkalı Factory) — server: /api/factory/storageRoles,
                permission'lar route bazında server ile birebir eşleşir */}
            <Route element={<RequirePermission permission="factory.overview.read" />}>
              <Route path="Fabrika" element={<FactoryOverviewPage />} />
            </Route>
            <Route element={<RequirePermission permission="factory.parts.read" />}>
              <Route path="Fabrika/Parcalar" element={<FactoryPartsPage />} />
            </Route>
            <Route element={<RequirePermission permission="factory.machines.read" />}>
              <Route path="Fabrika/Makineler" element={<FactoryMachinesPage />} />
            </Route>
            <Route element={<RequirePermission permission="factory.zones.read" />}>
              <Route path="Fabrika/Bolgeler" element={<FactoryZonesPage />} />
            </Route>
            <Route element={<RequirePermission permission="factory.catalog.manage" />}>
              <Route path="Fabrika/Katalog" element={<FactoryCatalogPage />} />
            </Route>
            {/* Belgeler (D-071) — panel izni factory.documents.manage.
                Belge GRUPLARI (factory.docs.*) tablete aittir, panelde
                sayfa açmaz (bkz. userCapability.js panel kapısı). */}
            <Route element={<RequirePermission permission="factory.documents.manage" />}>
              <Route path="Fabrika/Belgeler" element={<FactoryDocumentsPage />} />
            </Route>
            {/* Programlama İşlemleri — server ile AYNI izin
                (programming.read), bkz. factory-base.routes.js.
                Contract: panel-activity.md */}
            <Route element={<RequirePermission permission="programming.read" />}>
              <Route
                path="Fabrika/ProgramlamaIslemleri"
                element={<ProgramlamaIslemleriPage />}
              />
            </Route>
            <Route element={<RequireAuth allowedRoles={[ROLES.Admin]} />}>
              <Route path="EMQX" element={<EmqxPage />} />
              <Route path="BildirimGonder" element={<PushBroadcastPage />} />
            </Route>
            {/* İş Planlama — server: /api/tasks/storageRoles */}
            <Route element={<RequirePermission permission="tasks.access" />}>
              <Route path="IsPlanlama" element={<TasksPage />} />
            </Route>

            {/* Geliştirme (GitHub panosu, salt-okunur) — server: /api/development/storageRoles */}
            <Route element={<RequirePermission permission="development.read" />}>
              <Route path="Gelistirme" element={<DevelopmentPage />} />
            </Route>
            {/* Audit Log — server tarafı admin + developer kabul eder */}
            <Route element={<RequireAuth allowedRoles={[ROLES.Admin, "developer"]} />}>
              <Route path="AuditLog" element={<AuditLogPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={[
              "reports.firmBalance", "reports.firmNew", "reports.firmBilling",
              "reports.firmLocation",
              "reports.deviceConnection", "reports.deviceGeneral", "reports.deviceNew",
              "reports.deviceStatus", "reports.deviceServiceStatus", "reports.devicePerformance",
              "reports.devicesAtDate",
              "reports.firmDailyPerformance",
              "reports.firmPerformance",
              "reports.firmsPerformance",
            ]} />}>
              <Route path="Rapor" element={<ReportsLayout />}>
                <Route path="Menu" element={<ReportPage />} />
                <Route element={<RequirePermission permission="reports.devicePerformance" />}>
                  <Route path="CihazPerformans" element={<ReportDevicesPerformancePage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.deviceServiceStatus" />}>
                  <Route path="CihazServisHareket" element={<ReportDevicesServiceStatusBetweenDatesPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.deviceStatus" />}>
                  <Route path="CihazHareket" element={<ReportDevicesStatusBetweenDatesPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.devicesAtDate" />}>
                  <Route path="TarihtekiMakineler" element={<DevicesAtDateReportPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.deviceNew" />}>
                  <Route path="UretilenCihaz" element={<ReportNewDevicesBetweenDatesPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.deviceGeneral" />}>
                  <Route path="CihazGenel" element={<ReportDeviceGeneralInfo />} />
                </Route>
                <Route element={<RequirePermission permission="reports.deviceConnection" />}>
                  <Route path="CihazBaglantı" element={<ReportDeviceConnectionInfo />} />
                </Route>
                <Route element={<RequirePermission permission="reports.firmBalance" />}>
                  <Route path="FirmaBakiyeSeviye" element={<ReportFirmRemainderPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.firmNew" />}>
                  <Route path="FirmaAramızaKatılan" element={<ReportNewFirmsPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.firmLocation" />}>
                  <Route path="FirmaLokasyonaGöre" element={<ReportFirmListBasedLocationPage />} />
                </Route>
                <Route element={<RequirePermission permission="reports.firmBilling" />}>
                  <Route path="YüklenenFaturalar" element={<ReportFirmUploadedBillPage />} />
                </Route>
                {/* Dört performans raporu tek sayfada birleşti; dönem seçimle
                    belirlenir. İzinler bölünmüş halde kaldığı için kapı
                    "dördünden herhangi biri" olarak kurulur, sayfa da dönem
                    listesini izne göre filtreler. */}
                <Route
                  element={
                    <RequirePermission
                      anyOf={[
                        "reports.firmDailyPerformance",
                        "reports.firmPerformance",
                        "reports.firmRangePerformance",
                        "reports.firmsPerformance",
                      ]}
                    />
                  }
                >
                  <Route path="MusteriPerformans" element={<FirmPerformanceUnifiedReportPage />} />
                </Route>
                {/* Eski adresler — kayıtlı bookmark ve paylaşılmış linkler
                    bozulmasın diye yönlendirme bırakıldı. */}
                <Route path="MusteriGunlukPerformans" element={<Navigate to="/Anasayfa/Rapor/MusteriPerformans" replace />} />
                <Route path="FirmaPerformans" element={<Navigate to="/Anasayfa/Rapor/MusteriPerformans" replace />} />
                <Route path="FirmalarPerformans" element={<Navigate to="/Anasayfa/Rapor/MusteriPerformans" replace />} />
              </Route>
            </Route>
            <Route element={<RequirePermission permission="products.infoRead" />}>
              <Route path="ÜrünBilgisi" element={<ProductInfoPage />} />
            </Route>
            <Route element={<RequirePermission permission="products.typeRead" />}>
              <Route path="ÜrünTipi" element={<ProductTypePage />} />
            </Route>
            <Route element={<RequirePermission permission="parameters.townRead" />}>
              <Route path="İlçe" element={<TownPage />} />
            </Route>
            <Route element={<RequirePermission permission="parameters.cityRead" />}>
              <Route path="Şehir" element={<CityPage />} />
            </Route>
          </Route>

          {/* ═══ Dealer Routes ═══ */}
          {/* Dealer'a özel route'lar buraya eklenecek */}

          <Route path="*" element={<Page404 />} />
        </Route>
      </Route>

      <Route path="*" element={<Page404 />} />
      </Routes>
    </>
  );
}

export default App;
